import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  CheckCircle, 
  Palette, 
  Package, 
  Truck, 
  DollarSign,
  ArrowRight,
  ShoppingBag,
  Leaf,
  Award,
  Clock,
  Users,
  Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Footer from '@/components/home/Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomServicePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    royalty_percentage: 10
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/artcanvas`);
        if (res.data) {
          setSettings(prev => ({ ...prev, ...res.data }));
        }
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      }
    };
    fetchSettings();
  }, []);

  // 定制流程步骤
  const processSteps = [
    {
      icon: <Palette className="w-8 h-8" />,
      titleKey: 'customService.process.step1.title',
      descKey: 'customService.process.step1.desc',
      defaultTitle: '选择作品',
      defaultDesc: '在作品库中浏览并选择您喜欢的艺术作品'
    },
    {
      icon: <Package className="w-8 h-8" />,
      titleKey: 'customService.process.step2.title',
      descKey: 'customService.process.step2.desc',
      defaultTitle: '定制产品',
      defaultDesc: '选择产品类型、尺寸和数量，完成定制配置'
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      titleKey: 'customService.process.step3.title',
      descKey: 'customService.process.step3.desc',
      defaultTitle: '支付订单',
      defaultDesc: '通过安全的支付渠道完成订单付款'
    },
    {
      icon: <Truck className="w-8 h-8" />,
      titleKey: 'customService.process.step4.title',
      descKey: 'customService.process.step4.desc',
      defaultTitle: '配送到家',
      defaultDesc: '专业包装，全球配送，追踪物流信息'
    }
  ];

  // 产品特点
  const productFeatures = [
    {
      icon: <Leaf className="w-6 h-6" />,
      titleKey: 'customService.features.organic.title',
      descKey: 'customService.features.organic.desc',
      defaultTitle: '100%有机棉',
      defaultDesc: '采用GOTS认证有机棉，亲肤透气，环保可持续'
    },
    {
      icon: <Award className="w-6 h-6" />,
      titleKey: 'customService.features.quality.title',
      descKey: 'customService.features.quality.desc',
      defaultTitle: '高品质印刷',
      defaultDesc: '采用环保数码印刷技术，色彩鲜艳，持久不褪色'
    },
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      titleKey: 'customService.features.design.title',
      descKey: 'customService.features.design.desc',
      defaultTitle: '独特设计',
      defaultDesc: '每件产品都是独一无二的艺术品，彰显个人品味'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      titleKey: 'customService.features.delivery.title',
      descKey: 'customService.features.delivery.desc',
      defaultTitle: '快速交付',
      defaultDesc: '7-14个工作日完成生产，全程物流跟踪'
    }
  ];

  // 艺术家收益说明
  const artistBenefits = [
    {
      icon: <DollarSign className="w-5 h-5" />,
      text: t('customService.artist.benefit1', { percentage: settings.royalty_percentage })
    },
    {
      icon: <Users className="w-5 h-5" />,
      text: t('customService.artist.benefit2', '作品触达更多艺术爱好者')
    },
    {
      icon: <Star className="w-5 h-5" />,
      text: t('customService.artist.benefit3', '提升个人品牌影响力')
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-16" data-testid="custom-service-page">
      {/* Hero Section */}
      <section className="relative py-24 px-4 bg-gradient-to-br from-orange-50 to-orange-100 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-orange-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-300 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center">
            <Badge className="bg-orange-500 text-white rounded-full mb-6 px-4 py-1">
              {t('customService.hero.badge', '定制服务')}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-light text-amber-900 mb-6">
              {t('customService.hero.title', '让艺术走进生活')}
            </h1>
            <p className="text-xl text-amber-700 max-w-2xl mx-auto mb-10">
              {t('customService.hero.subtitle', '将您喜爱的艺术作品印制在100%有机棉产品上，打造独一无二的艺术生活方式')}
            </p>
            <Button 
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-10 py-6 text-lg"
              onClick={() => navigate('/artworks')}
              data-testid="hero-start-btn"
            >
              {t('customService.hero.cta', '开始定制')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-amber-900 mb-4">
              {t('customService.process.title', '定制流程')}
            </h2>
            <p className="text-amber-600 text-lg">
              {t('customService.process.subtitle', '简单四步，轻松完成定制')}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative text-center group">
                {/* 连接线 */}
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-gradient-to-r from-orange-300 to-orange-200" />
                )}
                
                {/* 步骤编号 */}
                <div className="relative z-10 w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  {step.icon}
                </div>
                
                <h3 className="text-xl font-medium text-amber-900 mb-3">
                  {t(step.titleKey, step.defaultTitle)}
                </h3>
                <p className="text-amber-600">
                  {t(step.descKey, step.defaultDesc)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Features */}
      <section className="py-24 px-4 bg-amber-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-amber-900 mb-4">
              {t('customService.features.title', '产品特点')}
            </h2>
            <p className="text-amber-600 text-lg">
              {t('customService.features.subtitle', '匠心品质，环保理念')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {productFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-5">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-medium text-amber-900 mb-2">
                  {t(feature.titleKey, feature.defaultTitle)}
                </h3>
                <p className="text-amber-600 text-sm leading-relaxed">
                  {t(feature.descKey, feature.defaultDesc)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Artist Benefits */}
      <section className="py-24 px-4 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-orange-500 text-white rounded-full mb-6 px-4 py-1">
                {t('customService.artist.badge', '艺术家收益')}
              </Badge>
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                {t('customService.artist.title', '与艺术家共享收益')}
              </h2>
              <p className="text-amber-500 text-lg mb-8 leading-relaxed">
                {t('customService.artist.desc', '我们相信艺术的价值应该与创作者共享。每一笔定制订单，原作艺术家都将获得版税收入，让艺术创作得到应有的回报。')}
              </p>

              <div className="space-y-4 mb-10">
                {artistBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center">
                      {benefit.icon}
                    </div>
                    <span className="text-amber-400">{benefit.text}</span>
                  </div>
                ))}
              </div>

              <Button 
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-amber-900 rounded-full px-8 py-6"
                onClick={() => navigate('/artcanvas')}
              >
                {t('customService.artist.cta', '了解更多')}
              </Button>
            </div>

            {/* Revenue Split Visual */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
              <h3 className="text-xl font-medium mb-6 text-center">
                {t('customService.artist.revenueTitle', '收益分成')}
              </h3>
              
              <div className="relative h-64 flex items-end justify-center gap-8">
                {/* Artist Bar */}
                <div className="flex flex-col items-center">
                  <div 
                    className="w-24 bg-gradient-to-t from-orange-500 to-orange-400 rounded-t-xl transition-all duration-500"
                    style={{ height: `${settings.royalty_percentage * 2}%` }}
                  />
                  <div className="mt-4 text-center">
                    <div className="text-2xl font-bold text-orange-400">{settings.royalty_percentage}%</div>
                    <div className="text-sm text-amber-500">{t('customService.artist.artistLabel', '艺术家')}</div>
                  </div>
                </div>

                {/* Platform Bar */}
                <div className="flex flex-col items-center">
                  <div 
                    className="w-24 bg-gradient-to-t from-gray-600 to-gray-500 rounded-t-xl transition-all duration-500"
                    style={{ height: `${(100 - settings.royalty_percentage) * 2}%` }}
                  />
                  <div className="mt-4 text-center">
                    <div className="text-2xl font-bold text-amber-500">{100 - settings.royalty_percentage}%</div>
                    <div className="text-sm text-amber-600">{t('customService.artist.platformLabel', '平台')}</div>
                  </div>
                </div>
              </div>

              <p className="text-center text-amber-600 text-sm mt-6">
                {t('customService.artist.revenueNote', '平台费用包含生产、印刷、包装、物流等成本')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light text-amber-900 mb-4">
              {t('customService.faq.title', '常见问题')}
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: t('customService.faq.q1', '定制产品的交付时间是多久？'),
                a: t('customService.faq.a1', '通常情况下，定制产品将在付款后7-14个工作日内完成生产并发货。具体时间可能因订单量和配送地区而有所不同。')
              },
              {
                q: t('customService.faq.q2', '产品的材质是什么？'),
                a: t('customService.faq.a2', '我们的产品采用100%GOTS认证有机棉，经过环保印染工艺处理，安全亲肤，可持续环保。')
              },
              {
                q: t('customService.faq.q3', '如何查看订单状态？'),
                a: t('customService.faq.a3', '登录您的账户，在个人中心的"我的订单"中可以查看所有订单状态和物流信息。')
              },
              {
                q: t('customService.faq.q4', '支持退换货吗？'),
                a: t('customService.faq.a4', '由于定制产品的特殊性，如非质量问题，我们不支持退换货。如收到产品有质量问题，请在7天内联系客服处理。')
              }
            ].map((item, index) => (
              <div key={index} className="bg-amber-50 rounded-2xl p-6">
                <h3 className="text-lg font-medium text-amber-900 mb-3 flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                    Q
                  </span>
                  {item.q}
                </h3>
                <p className="text-amber-700 pl-9">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-5xl font-light mb-6">
            {t('customService.cta.title', '开始您的艺术定制之旅')}
          </h2>
          <p className="text-xl text-white/80 mb-10">
            {t('customService.cta.subtitle', '探索作品库，找到属于您的艺术灵感')}
          </p>
          <Button 
            size="lg"
            className="bg-white text-orange-600 hover:bg-amber-100 rounded-full px-12 py-6 text-lg font-medium"
            onClick={() => navigate('/artworks')}
            data-testid="cta-browse-btn"
          >
            {t('customService.cta.button', '浏览作品')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomServicePage;
